import { axiosIns } from "../utils";
import { unwrapApiData } from "../utils/backendApi";

const toNumberIfPossible = (value) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  const next = Number(value);
  return Number.isNaN(next) ? value : next;
};

const getCollection = (payload, key) => {
  const data = unwrapApiData(payload);

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.[key])) {
    return data[key];
  }

  return [];
};

const normalizeCountry = (country) => ({
  ...country,
  id: toNumberIfPossible(country?.id),
  name: country?.name || "",
  iso2: country?.iso2 || "",
});

const normalizeProvince = (province, countryId) => ({
  ...province,
  id: toNumberIfPossible(province?.id),
  name: province?.name || "",
  iso2: province?.iso2 || "",
  country: toNumberIfPossible(
    province?.country ??
      province?.countryId ??
      province?.country_id ??
      countryId,
  ),
});

const normalizeCity = (city, province) => ({
  ...city,
  id: toNumberIfPossible(city?.id),
  name: city?.name || "",
  province: toNumberIfPossible(
    city?.province ?? city?.provinceId ?? city?.province_id ?? province?.id,
  ),
  provinceName: province?.name || city?.provinceName || "",
  country: toNumberIfPossible(province?.country),
  countryName: province?.countryName || city?.countryName || "",
});

const normalizeArrondissement = (arrondissement, city) => ({
  ...arrondissement,
  id: toNumberIfPossible(arrondissement?.id),
  name: arrondissement?.name || "",
  city: toNumberIfPossible(
    arrondissement?.city ??
      arrondissement?.cityId ??
      arrondissement?.city_id ??
      city?.id,
  ),
  cityName: city?.name || arrondissement?.cityName || "",
  province: toNumberIfPossible(city?.province ?? arrondissement?.province),
  provinceName: city?.provinceName || arrondissement?.provinceName || "",
  country: toNumberIfPossible(city?.country ?? arrondissement?.country),
  countryName: city?.countryName || arrondissement?.countryName || "",
});

const normalizeQuartier = (quartier, arrondissement) => ({
  ...quartier,
  id: toNumberIfPossible(quartier?.id),
  name: quartier?.name || "",
  arrondissement: toNumberIfPossible(
    quartier?.arrondissement ??
      quartier?.arrondissementId ??
      quartier?.arrondissement_id ??
      arrondissement?.id,
  ),
  arrondissementName:
    arrondissement?.name || quartier?.arrondissementName || "",
  city: toNumberIfPossible(arrondissement?.city ?? quartier?.city),
  cityName: arrondissement?.cityName || quartier?.cityName || "",
  province: toNumberIfPossible(arrondissement?.province ?? quartier?.province),
  provinceName: arrondissement?.provinceName || quartier?.provinceName || "",
  country: toNumberIfPossible(arrondissement?.country ?? quartier?.country),
  countryName: arrondissement?.countryName || quartier?.countryName || "",
});

export const fetchCountries = async () => {
  const response = await axiosIns.get("/geography/countries");
  return getCollection(response?.data ?? response, "countries").map(
    normalizeCountry,
  );
};

export const fetchProvincesByCountry = async (countryId) => {
  if (!countryId) {
    return [];
  }

  const response = await axiosIns.get(
    `/geography/countries/${countryId}/provinces`,
  );
  return getCollection(response?.data ?? response, "provinces").map(
    (province) => normalizeProvince(province, countryId),
  );
};

export const fetchCitiesByProvince = async (province) => {
  const provinceId =
    typeof province === "object" ? province?.id : toNumberIfPossible(province);

  if (!provinceId) {
    return [];
  }

  const response = await axiosIns.get(
    `/geography/provinces/${provinceId}/cities`,
  );
  return getCollection(response?.data ?? response, "cities").map((city) =>
    normalizeCity(
      city,
      typeof province === "object" ? province : { id: provinceId },
    ),
  );
};

export const fetchArrondissementsByCity = async (city) => {
  const cityId = typeof city === "object" ? city?.id : toNumberIfPossible(city);

  if (!cityId) {
    return [];
  }

  const response = await axiosIns.get(
    `/geography/cities/${cityId}/arrondissements`,
  );
  return getCollection(response?.data ?? response, "arrondissements").map(
    (arrondissement) =>
      normalizeArrondissement(
        arrondissement,
        typeof city === "object" ? city : { id: cityId },
      ),
  );
};

export const fetchQuartiersByArrondissement = async (arrondissement) => {
  const arrondissementId =
    typeof arrondissement === "object"
      ? arrondissement?.id
      : toNumberIfPossible(arrondissement);

  if (!arrondissementId) {
    return [];
  }

  const response = await axiosIns.get(
    `/geography/arrondissements/${arrondissementId}/quartiers`,
  );
  return getCollection(response?.data ?? response, "quartiers").map(
    (quartier) =>
      normalizeQuartier(
        quartier,
        typeof arrondissement === "object"
          ? arrondissement
          : { id: arrondissementId },
      ),
  );
};

export const fetchArrondissements = async () => {
  const response = await axiosIns.get("/geography/arrondissements");
  return getCollection(response?.data ?? response, "arrondissements").map((a) =>
    normalizeArrondissement(a, {
      id: a.city,
      name: a.cityName,
      province: a.province,
      provinceName: a.provinceName,
      country: a.country,
      countryName: a.countryName,
    }),
  );
};

export const fetchArrondissementById = async (id) => {
  if (!id) return null;
  const response = await axiosIns.get(`/geography/arrondissements/${id}`);
  const data = unwrapApiData(response?.data ?? response);
  const a = data?.arrondissement || data;
  if (!a) return null;
  return normalizeArrondissement(a, {
    id: a.city,
    name: a.cityName,
    province: a.province,
    provinceName: a.provinceName,
    country: a.country,
    countryName: a.countryName,
  });
};

export const fetchQuartiers = async () => {
  const response = await axiosIns.get("/geography/quartiers");
  return getCollection(response?.data ?? response, "quartiers").map((q) =>
    normalizeQuartier(q, {
      id: q.arrondissement,
      name: q.arrondissementName,
      city: q.city,
      cityName: q.cityName,
      province: q.province,
      provinceName: q.provinceName,
      country: q.country,
      countryName: q.countryName,
    }),
  );
};

export const fetchQuartierById = async (id) => {
  if (!id) return null;
  const response = await axiosIns.get(`/geography/quartiers/${id}`);
  const data = unwrapApiData(response?.data ?? response);
  const q = data?.quartier || data;
  if (!q) return null;
  return normalizeQuartier(q, {
    id: q.arrondissement,
    name: q.arrondissementName,
    city: q.city,
    cityName: q.cityName,
    province: q.province,
    provinceName: q.provinceName,
    country: q.country,
    countryName: q.countryName,
  });
};

export const createArrondissementApi = async ({ name, cityId }) => {
  const response = await axiosIns.post(
    `/geography/cities/${cityId}/arrondissements`,
    {
      name,
    },
  );
  return unwrapApiData(response?.data ?? response);
};

export const updateArrondissementApi = async ({ id, name }) => {
  const response = await axiosIns.put(`/geography/arrondissements/${id}`, {
    name,
  });
  return unwrapApiData(response?.data ?? response);
};

export const deleteArrondissementApi = async (id) => {
  const response = await axiosIns.delete(`/geography/arrondissements/${id}`);
  return unwrapApiData(response?.data ?? response);
};

export const createQuartierApi = async ({ name, arrondissementId }) => {
  const response = await axiosIns.post(
    `/geography/arrondissements/${arrondissementId}/quartiers`,
    { name },
  );
  return unwrapApiData(response?.data ?? response);
};

export const updateQuartierApi = async ({ id, name }) => {
  const response = await axiosIns.put(`/geography/quartiers/${id}`, { name });
  return unwrapApiData(response?.data ?? response);
};

export const deleteQuartierApi = async (id) => {
  const response = await axiosIns.delete(`/geography/quartiers/${id}`);
  return unwrapApiData(response?.data ?? response);
};
